import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import cx from 'clsx'
import { useCsrf } from '../../utils/context/CsrfContext'
import { isNumber, formatPhone } from '../../utils/functions'
import * as styles from '../../styles/components/slide-out-contact-form.module.scss'
import { PANEL_ID } from './FloatingContactButton'
import { useAnalytics } from '../../utils/analytics/useAnalytics'
import {
  clearStoredConsultantInfo,
  dispatchConsultantInfoChange,
  getStoredConsultantInfo,
  markConsultantZipOverride,
  saveStoredConsultantInfo,
} from '../../utils/consultant-info-session'

const TITLE_ID = 'global-contact-form-title'
const sanitizeContactLink = (href) => {
  if (!href || typeof href !== 'string') return ''
  if (href.startsWith('mailto:')) return 'mailto:'
  if (href.startsWith('tel:')) return 'tel:'
  return href
}

const SlideOutContactForm = ({ open, onClose }) => {
  const [zipcode, setZipcode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [lookupError, setLookupError] = useState(null)
  const [consultantInfo, setConsultantInfo] = useState(null)
  const zipInputRef = useRef(null)
  const { track } = useAnalytics({
    component: { name: 'SlideOutContactForm', description: 'Slide-out contact form' },
  })

  const consultants = useMemo(() => {
    if (!consultantInfo) return []
    const make = (key, label) => {
      const name = consultantInfo?.[key]
      if (!name) return null
      const email = consultantInfo?.[key === 'ric' ? 'email' : `${key}_email`]
      const phone = consultantInfo?.[`${key}_phone`]
      const calendar = consultantInfo?.[`${key}_calendar`]
      const photo = consultantInfo?.[`${key}_photo`]
      const initials = name
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase())
        .slice(0, 2)
        .join('') || name.slice(0, 1).toUpperCase()
      return {
        key,
        name,
        role: label,
        email,
        phone,
        calendar,
        photo,
        initials,
      }
    }
    return [
      make('ric', 'Regional Investment Consultant'),
      make('bda', 'Business Development Associate'),
    ].filter(Boolean)
  }, [consultantInfo])

  const { ensure: ensureCsrf } = useCsrf()

  const updateConsultantInfo = useCallback((data, options = {}) => {
    setConsultantInfo(data)
    setLookupError(null)
    setZipcode(data?.zipcode || '')

    if (data) {
      saveStoredConsultantInfo(data)
      if (options.source === 'manual') {
        markConsultantZipOverride(data?.zipcode)
      }
    } else {
      clearStoredConsultantInfo()
    }

    dispatchConsultantInfoChange(data)
  }, [])

  const handleClose = useCallback((source = 'close_button') => {
    track('contact_form_close', {
      interaction: { type: source },
    })
    if (onClose) {
      onClose()
    }
  }, [onClose, track])

  const handleZipChange = useCallback((e) => {
    const { value } = e.target
    if (value.length <= 5 && isNumber(value)) {
      markConsultantZipOverride(value)
      setZipcode(value)
      setLookupError(null)
    }
  }, [])

  const handleBackdropKeyDown = useCallback((e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      handleClose('backdrop')
    }
  }, [handleClose])
  const handleBackdropClick = useCallback(() => {
    handleClose('backdrop')
  }, [handleClose])
  const handleHeaderCloseClick = useCallback(() => {
    handleClose('close_button')
  }, [handleClose])
  const handleFooterCloseClick = useCallback(() => {
    handleClose('footer_button')
  }, [handleClose])

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault()
    if (zipcode.length !== 5) {
      track('contact_zip_error', {
        interaction: { type: 'validation' },
        content: { zipLength: zipcode.length },
      })
      return
    }

    setIsSubmitting(true)
    setLookupError(null)
    
    try {
      const csrf = await ensureCsrf()
      const res = await fetch('/api/zipcode-lookup', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-csrf-token': csrf,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          zipcode: zipcode,
          skipRecaptcha: true,
        }),
      })
      
      const json = await res.json()
      if (json.status !== 'success') {
        throw new Error(json.errors?.[0] || 'Error looking up consultant')
      }
      
      markConsultantZipOverride(zipcode)
      updateConsultantInfo(json.data, { source: 'manual' })
      const consultantCount = ['ric', 'bda'].reduce(
        (count, key) => (json.data?.[key] ? count + 1 : count),
        0
      )
      track('contact_zip_submit', {
        interaction: { type: 'submit' },
        content: { zipLength: zipcode.length, consultantCount },
      })
      track('cta_complete', {
        interaction: { type: 'zip_submit' },
      })
    } catch (err) {
      setLookupError(err.message)
      track('contact_zip_error', {
        interaction: { type: 'lookup' },
        content: { errorCategory: 'lookup_failed' },
      })
    } finally {
      setIsSubmitting(false)
    }
  }, [zipcode, ensureCsrf, updateConsultantInfo, track])

  const handleEdit = useCallback(() => {
    track('contact_edit_search', {
      interaction: { type: 'click', text: 'Edit search' },
    })
    markConsultantZipOverride(consultantInfo?.zipcode || zipcode)
    updateConsultantInfo(null)
  }, [consultantInfo?.zipcode, updateConsultantInfo, track, zipcode])
  const handleConsultantActionClick = useCallback((event) => {
    const { action, consultantName, consultantRole } = event.currentTarget.dataset
    const href = event.currentTarget.getAttribute('href') || ''
    const sanitizedHref = sanitizeContactLink(href)
    const payload = {
      interaction: {
        type: 'click',
        linkUrl: sanitizedHref,
      },
      content: {
        consultantName,
        consultantRole,
      },
    }

    if (action === 'phone') {
      track('contact_consultant_phone_click', payload)
    } else if (action === 'email') {
      track('contact_consultant_email_click', payload)
    } else if (action === 'schedule') {
      track('contact_consultant_schedule_click', payload)
    }

    track('cta_complete', {
      interaction: { type: 'consultant_action', text: action || '' },
      content: {
        consultantName,
        consultantRole,
      },
    })
  }, [track])

  useEffect(() => {
    if (!open) return

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleClose('escape')
      }
    }

    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [open, handleClose])

  useEffect(() => {
    if (typeof document === 'undefined') return undefined
    if (!open) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    if (typeof window === 'undefined') return

    const stored = getStoredConsultantInfo()
    if (stored) {
      updateConsultantInfo(stored)
    }
  }, [open, updateConsultantInfo])

  useEffect(() => {
    if (!open) return

    if (zipInputRef.current && !consultantInfo) {
      const id = requestAnimationFrame(() => {
        zipInputRef.current?.focus()
      })
      return () => cancelAnimationFrame(id)
    }

    return undefined
  }, [open, consultantInfo])

  const hasConsultantResults = consultants.length > 0
  const zipHasError = zipcode.length > 0 && zipcode.length < 5

  return (
    <>
      <div
        className={`${styles.slideOutContactFormBackdrop} ${open ? styles.slideOutContactFormBackdropOpen : ''}`}
        role="button"
        tabIndex={open ? 0 : -1}
        onClick={handleBackdropClick}
        onKeyDown={handleBackdropKeyDown}
        aria-label="Close contact form"
        aria-hidden={!open}
      />

      <aside
        id={PANEL_ID}
        className={`${styles.slideOutContactForm} ${open ? styles.slideOutContactFormOpen : ''}`}
        aria-hidden={!open}
        aria-modal={open}
        role="dialog"
        aria-labelledby={TITLE_ID}
      >
        <div className={styles.slideOutContactFormHeader}>
          <h2 id={TITLE_ID} className={styles.slideOutContactFormTitle}>Contact Us</h2>
          <button onClick={handleHeaderCloseClick} className={styles.slideOutContactFormCloseButton} aria-label="Close contact form">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div className={styles.slideOutContactFormContent}>
          <div className={styles.slideOutContactFormIntro}>
            <p className={styles.slideOutContactFormEyebrow}>For Institutional Investors Only</p>
            <h3 className={styles.slideOutContactFormHeadline}>Locate Your Harbor Consultant</h3>
            <p className={styles.slideOutContactFormSubtitle}>
              Enter your ZIP code to connect with your dedicated Harbor team.
            </p>
          </div>

          {!hasConsultantResults && (
            <div className={styles.slideOutContactFormSurface}>
              <form onSubmit={handleSubmit} className={styles.slideOutContactFormForm}>
                <div
                  className={cx(
                    styles.slideOutContactFormInputContainer,
                    zipHasError && styles.slideOutContactFormInputContainerError,
                    isSubmitting && styles.slideOutContactFormInputContainerBusy
                  )}
                >
                  <input
                    ref={zipInputRef}
                    placeholder="ZIP Code*"
                    value={zipcode}
                    onChange={handleZipChange}
                    className={cx(
                      styles.slideOutContactFormZipInput,
                      zipHasError && styles.slideOutContactFormZipInputError
                    )}
                    maxLength={5}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    aria-describedby={zipHasError ? `${PANEL_ID}-zip-helper` : undefined}
                  />
                  <button
                    type="submit"
                    disabled={zipcode.length < 5 || isSubmitting}
                    className={styles.slideOutContactFormSubmitButton}
                  >
                    {isSubmitting ? 'Searching...' : 'Submit'}
                  </button>
                </div>

                {zipHasError && (
                  <p className={styles.slideOutContactFormErrorText} id={`${PANEL_ID}-zip-helper`}>
                    Enter a valid 5-digit ZIP code.
                  </p>
                )}
              </form>

              <p className={styles.slideOutContactFormInstructions}>
                We use your ZIP code to introduce the Harbor professionals dedicated to your region.
              </p>

              {lookupError && (
                <div className={styles.slideOutContactFormErrorDisplay} role="alert">
                  {lookupError}
                </div>
              )}
            </div>
          )}

          {hasConsultantResults && (
            <div className={styles.slideOutContactFormSurface}>
              <div className={styles.slideOutContactFormResultHeader}>
                <span className={styles.slideOutContactFormResultBadge}>Match Found</span>
                <h4 className={styles.slideOutContactFormResultTitle}>Your Harbor consultant team</h4>
                <p className={styles.slideOutContactFormResultDescription}>
                  Reach out directly or edit your search below.
                </p>
              </div>

              <div className={styles.slideOutContactFormConsultantList}>
                {consultants.map((consultant) => (
                  <article key={consultant.key} className={styles.slideOutContactFormConsultantCard}>
                    <div className={styles.slideOutContactFormConsultant}>
                      <div className={styles.slideOutContactFormConsultantAvatar}>
                        {consultant.photo ? (
                          <img src={consultant.photo} alt={consultant.name} width="72" height="72" loading="lazy" />
                        ) : (
                          <span>{consultant.initials}</span>
                        )}
                      </div>
                      <div className={styles.slideOutContactFormConsultantBody}>
                        <div className={styles.slideOutContactFormConsultantHeader}>
                          <p className={styles.slideOutContactFormConsultantName}>{consultant.name}</p>
                        {consultant.phone && (
                          <a
                            href={`tel:${consultant.phone}`}
                            className={styles.slideOutContactFormConsultantPhone}
                            onClick={handleConsultantActionClick}
                            data-action="phone"
                            data-consultant-name={consultant.name}
                            data-consultant-role={consultant.role}
                          >
                            {formatPhone(consultant.phone)}
                          </a>
                        )}
                        </div>
                      </div>
                    </div>
                    <div className={styles.slideOutContactFormConsultantRoleRow}>
                      <p className={styles.slideOutContactFormConsultantRole}>{consultant.role}</p>
                    </div>
                    {(consultant.email || consultant.calendar) && (
                      <div className={styles.slideOutContactFormConsultantActions}>
                        {consultant.email && (
                          <a
                            href={`mailto:${consultant.email}`}
                            className={styles.slideOutContactFormConsultantAction}
                            onClick={handleConsultantActionClick}
                            data-action="email"
                            data-consultant-name={consultant.name}
                            data-consultant-role={consultant.role}
                          >
                            Email
                          </a>
                        )}
                        {consultant.calendar && (
                          <a
                            href={consultant.calendar}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.slideOutContactFormConsultantAction}
                            onClick={handleConsultantActionClick}
                            data-action="schedule"
                            data-consultant-name={consultant.name}
                            data-consultant-role={consultant.role}
                          >
                            Schedule
                          </a>
                        )}
                      </div>
                    )}
                  </article>
                ))}
              </div>

              <div className={styles.slideOutContactFormZipCodeInfo}>
                <div className={styles.slideOutContactFormZipCodeRow}>
                  <span className={styles.slideOutContactFormZipGlyph} aria-hidden="true">📍</span>
                  <p className={styles.slideOutContactFormZipCodeText}>
                    Zip code: <strong>{consultantInfo?.zipcode || zipcode}</strong>
                  </p>
                </div>
                <button onClick={handleEdit} className={styles.editButton}>
                  Edit search
                </button>
              </div>

              <p className={styles.slideOutContactFormDisclaimer}>
                The Harbor Investment Consultant listed is based on the ZIP code provided and is intended for institutional investors only.
              </p>
            </div>
          )}

          <div className={styles.slideOutContactFormFooterActions}>
            <button
              type="button"
              className={styles.slideOutContactFormCloseAction}
              onClick={handleFooterCloseClick}
            >
              Close Form
            </button>
          </div>

          <div className={styles.slideOutContactFormContactLink}>
            <p>
              Need additional help?{' '}<br/>
              <a href="/contact-us/" target="_blank" rel="noopener noreferrer">
                Visit our Contact page
              </a>
            </p>
          </div>
        </div>
      </aside>
    </>
  )
}

SlideOutContactForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default SlideOutContactForm
